import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Smartphone, ArrowRight } from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";

const FAQS = [
  { q: "Do I really not need a laptop?", a: "Correct. Every step — describing your site, previewing it, editing it, and deploying it live — happens on your phone. HandsOn was designed thumb-first, not as a shrunk-down desktop app." },
  { q: "What can I build?", a: "Portfolios, storefronts, booking pages, waitlists, event pages, restaurant sites, and more. If you can describe it, the AI can build a first version of it." },
  { q: "Is the deployed site a real website?", a: "Yes. When you tap Deploy, your site is published to a genuine live URL you can share with anyone, bookmark, or hand to a client." },
  { q: "How does tap-to-edit work?", a: "In the live preview, tap any element you want to change. A bottom sheet opens where you describe the change in plain language, and the AI rewrites just that piece." },
  { q: "Can I keep editing after publishing?", a: "Absolutely. Editing is iterative and continuous — each change is a quick message, and you can redeploy anytime." },
  { q: "Who is HandsOn for?", a: "Freelancers, agencies, small business owners, and anyone who lives on their phone and needs to get online fast without hiring a developer." },
];

export default function WhyPhoneFirst() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(0);

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-6xl px-6 pt-14 pb-6 md:pt-20">
        <div className="grid items-center gap-10 md:grid-cols-2">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-ho-primary">Why phone-first</span>
            <h1 className="font-display mt-4 text-4xl font-black tracking-tighter md:text-6xl">
              The next billion builders won't own a laptop.
            </h1>
            <p className="mt-5 max-w-md text-lg text-ho-muted">
              For most of the world, the phone isn't a secondary device — it's the only device. HandsOn is built for that reality, not against it.
            </p>
            <button onClick={() => navigate("/login")} data-testid="why-cta"
              className="tap-scale mt-7 inline-flex items-center gap-2 rounded-full bg-ho-primary px-6 py-3.5 font-semibold text-white">
              Build on your phone <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1653762370943-2d6810061c5f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000"
              alt="Building on a phone in a cafe"
              className="w-full rounded-[32px] border border-black/5 object-cover shadow-xl aspect-[4/5]" />
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid gap-4 md:grid-cols-3">
            {[
              { h: "Thumb-first, always", b: "Big touch targets, bottom sheets, and swipe gestures. Nothing hides behind tiny icons or requires a mouse." },
              { h: "No pinch, no zoom", b: "The preview fills your screen at true scale. You see exactly what your visitors will see." },
              { h: "Momentum matters", b: "Idea to live URL in minutes. Phone-first isn't a compromise — it's a faster way to ship." },
            ].map((c, i) => (
              <motion.div key={c.h}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="rounded-3xl border border-black/5 bg-ho-bg p-6">
                <div className="grid place-items-center w-11 h-11 rounded-2xl bg-ho-primary/10">
                  <Smartphone className="w-5 h-5 text-ho-primary" />
                </div>
                <h3 className="font-display mt-4 text-lg font-bold">{c.h}</h3>
                <p className="mt-2 text-[15px] text-ho-muted">{c.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-16">
        <h2 className="font-display text-center text-3xl font-bold tracking-tight md:text-4xl">Frequently asked</h2>
        <div className="mt-8 space-y-3">
          {FAQS.map((f, i) => (
            <div key={f.q} className="overflow-hidden rounded-3xl border border-black/5 bg-white">
              <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-${i}`}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left">
                <span className="font-display text-[17px] font-semibold">{f.q}</span>
                <ChevronDown className={`w-5 h-5 shrink-0 text-ho-muted transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}>
                    <p className="px-6 pb-5 text-[15px] leading-relaxed text-ho-muted">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
